/**
 * ParallelCalcDebugPanel — admin-only side panel exposing every intermediate
 * calculation step for a product, for diagnostic use against the
 * profitability engine.
 *
 * Day 47 (Decision 16) — hybrid B3 architecture: reads persisted columns
 * from product_profitability for the ~80% already exposed, calls the
 * calculate_breakdown_extras_regrounded RPC (U10 re-point; U9 S4 successor)
 * for the genuinely-missing pieces (junction supply cost, per-path fee
 * disaggregation, per-path intermediates, Buy Score components).
 *
 * Width 560px (override of the standard 320px slide-in per
 * /standards/COMPONENT_STANDARDS.md §4) because horizontal density of
 * per-path data exceeds the standard panel width — see Decision 16
 * Tradeoffs.
 *
 * Three-layer admin guard:
 *   Layer 1: useGlobalKeyboardShortcuts — silent shortcut for non-admins
 *   Layer 2: this component returns null when !isAdmin
 *   Layer 3: calculate_breakdown_extras_regrounded RPC raises 'admin only' server-side
 */
import { useCallback, useEffect, useMemo, useState, type JSX } from 'react';
import { X, Search, AlertTriangle } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';
import { useApp } from '../../hooks/useApp';
import { useSettingsConfig } from '../../hooks/useSettingsConfig';
import { useDebugPanel } from '../../hooks/useDebugPanel';
import DebugPanelSection from './DebugPanelSection';

// ── Default Buy Score weights for the Debug Panel ──────────────────────────
// Day 70 P3.3 — keys pinned to bswc.key form (engine LEFT JOINs bswc ON
// bswc.key per Day 68 Q1 Path B; frontend sends keys verbatim, engine
// dispatches via bswc.applies_to_metric). Day 96 (P8.3) — re-proportioned to
// the canonical 7-key set so the panel exercises every is_available=true wired
// key incl. competition_level (Day 68) + demand_score + days_of_supply (Day 96).
// (The verify-script lockstep partners retired at the U10 :327 depth slice —
// F3b; the successor weight literal lives in the oracle differential + depth
// suites, exercised against the independent oracle every push.) The panel's
// buy_score_final mirrors the persisted normalized score within ±0.01.
const DEFAULT_DEBUG_WEIGHTS: Record<string, number> = {
  roi_pct: 0.25,
  ppu: 0.2,
  profit_per_day: 0.2,
  sales_velocity: 0.15,
  competition_level: 0.1,
  demand_score: 0.05,
  days_of_supply: 0.05,
};

// ── Path display config ────────────────────────────────────────────────────
const ALL_PATHS: ReadonlyArray<{ key: string; label: string }> = [
  { key: 'amazon_fba', label: 'FBA' },
  { key: 'amazon_fbm', label: 'FBM' },
  { key: 'ebay', label: 'eBay' },
  { key: 'walmart_self', label: 'WMT Self' },
  { key: 'walmart_wfs', label: 'WFS' },
  { key: 'shopify', label: 'Shopify' },
] as const;

// Day 60.5 — `activeKeyToMarketplace` deleted. activePathKeys returns the
// canonical long form (amazon_fba / amazon_fbm / ...) post-Day-60a, and
// po_staging / po_line_items now also store long form post-Day-60.5, so
// the prior short→long conversion is identity. Filter visiblePaths
// directly against ALL_PATHS.key.

// ── Formatting per /standards/DATA_STANDARDS.md ────────────────────────────
function fmt$(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}
function fmtPct(v: unknown, decimals = 1): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(decimals)}%`;
}
function fmtDays(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return `${n.toFixed(1)}d`;
}
function fmtNum(v: unknown, decimals = 2): string {
  if (v === null || v === undefined || v === '') return '—';
  const n = Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(decimals);
}

// ── Types for fetched data ─────────────────────────────────────────────────
interface PpRow {
  product_id: string;
  /** pp's stored column (public.marketplace enum — slice-B family);
   *  value space is fpc path keys — keys the per-path table below. */
  marketplace: string;
  sale_price: number | null;
  cost_usd: number | null;
  cost_product: number | null;
  cost_supplier_shipping: number | null;
  cost_inbound_shipping: number | null;
  cost_prep_fee: number | null;
  cost_misc: number | null;
  total_cost_per_unit: number | null;
  marketplace_fees: number | null;
  returns_cost: number | null;
  profit_per_unit: number | null;
  roi_pct: number | null;
  margin_pct: number | null;
  monthly_sales: number | null;
  days_to_sell: number | null;
  days_credit_locked_velocity: number | null;
  days_credit_locked_safety: number | null;
  profit_per_day_velocity: number | null;
  profit_per_day_safety: number | null;
  roi_per_day_velocity: number | null;
  roi_per_day_safety: number | null;
  effective_floor: number | null;
  buy_score: number | null;
}

// Successor per-path envelope (calculate_breakdown_extras_regrounded, U10
// re-point). Money fields are kind-4 honest-NULL when an input is unknown;
// deductions_before_fees died with the legacy formula (returns enter as the
// explicit returns_cost_recomputed leg of net_after_fees).
interface PerPathBreakdown {
  sale_price: number | null;
  sale_price_source: string | null;
  referral_fee: number | null;
  path_fee_label: string | null;
  path_fee: number | null;
  outbound_shipping: number | null;
  total_fees: number | null;
  gross_revenue: number | null;
  adjusted_revenue: number | null;
  returns_cost_recomputed: number | null;
  total_cost_recomputed: number | null;
  buffered_cost_recomputed: number | null;
  days_to_sell: number | null;
  payout_days: number | null;
  net_after_fees: number | null;
  persisted_marketplace_fees: number | null;
  persisted_profit_per_unit: number | null;
  persisted_total_cost: number | null;
  persisted_returns_cost: number | null;
  below_map: boolean | null;
}

// Successor score decomposition: raw/normalized/weighted are honest-NULL for
// unknown metrics (R1-R2(c) unknown-propagation); in_weighted_set marks the
// L3a divisor membership.
interface ScoreComponent {
  raw: number | null;
  normalized: number | null;
  weight: number;
  penalty: boolean;
  weighted: number | null;
  applies_to_metric: string | null;
  is_available: boolean;
  in_weighted_set: boolean;
}

// Successor top-level envelope. safety_buffer_dollar_amount and
// cost_multiplier died with the legacy formula (the buffer applies per-path —
// buffered_cost_recomputed vs total_cost_recomputed); supply_cost is the
// junction-grounded recompute (primary product_distributors row).
interface ExtrasResponse {
  product_id: string;
  distributor_id: string;
  supply_cost: number | null;
  safety_buffer_pct: number | null;
  estimated_tax_pct: number | null;
  map_price: number | null;
  enforces_map: boolean;
  on_hand: number | null;
  days_of_supply: number | null;
  per_path: Record<string, PerPathBreakdown>;
  buy_score_components: Record<string, ScoreComponent>;
  buy_score_final: number | null;
  persisted_buy_score: number | null;
  active_weights: Record<string, number> | null;
  safety_cap_mode: boolean;
  computed_at: string;
}

// U10 cost re-point — cost_usd allowlisted OUT of both lookups (slice-B drop
// family): the panel's cost display reads pp's own snapshot (sampleRow), never
// the products read-copy; the fetched field was dead weight.
interface ProductLookup {
  id: string;
  isg_sku: string;
  title: string | null;
}

// The successor RPC is in the generated Database types (unlike the legacy
// fn this panel launched against), so the call goes through supabase.rpc
// directly — the former LooseRpc widening is retired with the U10 re-point.

// ── Component ──────────────────────────────────────────────────────────────

export default function ParallelCalcDebugPanel(): JSX.Element | null {
  const { isAdmin } = useApp();
  const { activePathKeys } = useSettingsConfig();
  const { open, selectedProductId, setSelectedProductId, closePanel } = useDebugPanel();

  const [skuInput, setSkuInput] = useState<string>('');
  const [product, setProduct] = useState<ProductLookup | null>(null);
  const [ppRows, setPpRows] = useState<PpRow[]>([]);
  const [extras, setExtras] = useState<ExtrasResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Determine which paths to render columns for. Use activePathKeys when
  // available; fall back to ALL_PATHS so the panel still works in
  // environments where settings haven't loaded.
  const visiblePaths = useMemo(() => {
    if (!activePathKeys || activePathKeys.length === 0) return ALL_PATHS;
    const activePathSet = new Set(activePathKeys);
    return ALL_PATHS.filter((p) => activePathSet.has(p.key));
  }, [activePathKeys]);

  // Lookup product by SKU and trigger data fetch.
  const loadProductBySku = useCallback(
    async (sku: string): Promise<void> => {
      setError(null);
      if (!sku.trim()) return;
      const { data, error: lookupErr } = await supabase
        .from('products')
        .select('id, isg_sku, title')
        .eq('isg_sku', sku.trim())
        .maybeSingle();
      if (lookupErr) {
        setError(`Lookup failed: ${lookupErr.message}`);
        return;
      }
      if (!data) {
        setError(`No product with ISG SKU "${sku.trim()}"`);
        return;
      }
      setProduct(data as ProductLookup);
      setSelectedProductId((data as ProductLookup).id);
    },
    [setSelectedProductId]
  );

  // Lookup product by id (e.g. when set externally via context).
  useEffect(() => {
    if (!selectedProductId) {
      setProduct(null);
      setPpRows([]);
      setExtras(null);
      return;
    }
    if (product?.id === selectedProductId) return;
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('products')
        .select('id, isg_sku, title')
        .eq('id', selectedProductId)
        .maybeSingle();
      if (!cancelled && data) {
        setProduct(data as ProductLookup);
        setSkuInput((data as ProductLookup).isg_sku ?? '');
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedProductId, product?.id]);

  // Fetch product_profitability + breakdown extras.
  useEffect(() => {
    if (!open || !selectedProductId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const [ppResult, extrasResult] = await Promise.all([
          supabase.from('product_profitability').select('*').eq('product_id', selectedProductId),
          supabase.rpc('calculate_breakdown_extras_regrounded', {
            p_product_id: selectedProductId,
            p_weights: DEFAULT_DEBUG_WEIGHTS,
            p_safety_cap: true,
          }),
        ]);
        if (cancelled) return;
        if (ppResult.error) throw new Error('product_profitability: ' + ppResult.error.message);
        if (extrasResult.error)
          throw new Error('calculate_breakdown_extras_regrounded: ' + extrasResult.error.message);
        setPpRows((ppResult.data as PpRow[]) ?? []);
        setExtras(extrasResult.data as unknown as ExtrasResponse);
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, selectedProductId]);

  // Layer 2 of the admin guard: render nothing for non-admins regardless of
  // open state. Layer 1 (shortcut handler) blocks open() for non-admins; this
  // is defense-in-depth. Placed AFTER hook calls per rules-of-hooks.
  if (!isAdmin) return null;
  if (!open) return null;

  const ppByPath: Record<string, PpRow> = {};
  for (const r of ppRows) ppByPath[r.marketplace] = r;
  const sampleRow = ppRows[0];

  return (
    <>
      {/* Backdrop — dim, non-blocking. Click outside dismisses. */}
      <button
        type="button"
        className="fixed inset-0 bg-black/30 z-[var(--z-modal,200)] cursor-default"
        onClick={closePanel}
        aria-label="Close debug panel"
        tabIndex={-1}
      />
      {/* Panel — 560px slide-in from right (override of standard 320px;
          see Decision 16 Tradeoffs). */}
      <aside
        role="dialog"
        aria-modal="false"
        aria-label="Parallel Calculation Debug Panel"
        data-testid="debug-panel-root"
        className="fixed right-0 top-0 bottom-0 z-[calc(var(--z-modal,200)+1)] flex flex-col bg-surface-1 border-l border-border shadow-2xl"
        style={{ width: 560 }}
      >
        {/* Header */}
        <div className="flex-shrink-0 px-4 py-3 border-b border-border flex items-center justify-between bg-surface-2">
          <div>
            <h2 className="text-sm font-semibold text-ink-bright">Parallel Calculation Debug</h2>
            <p className="text-2xs text-ink-muted">Shift+Ctrl+D · Admin only</p>
          </div>
          <button
            onClick={closePanel}
            className="p-1.5 rounded text-ink-muted hover:text-ink-primary hover:bg-surface-3"
            aria-label="Close panel"
          >
            <X size={16} />
          </button>
        </div>

        {/* Product selector */}
        <div className="flex-shrink-0 px-4 py-3 bg-surface-2 border-b border-border-subtle">
          <div className="flex items-center gap-2">
            <Search size={14} className="text-ink-muted flex-shrink-0" />
            <input
              type="text"
              value={skuInput}
              onChange={(e) => setSkuInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') loadProductBySku(skuInput);
              }}
              placeholder="ISG SKU (e.g. ISG-000123) — Enter to load"
              className="arbi-input flex-1 text-xs"
              aria-label="Product ISG SKU"
            />
            <button
              onClick={() => loadProductBySku(skuInput)}
              className="arbi-btn-primary text-xs"
              disabled={!skuInput.trim() || loading}
            >
              Load
            </button>
          </div>
          {product && (
            <p className="text-2xs text-ink-muted mt-1.5">
              <span className="font-mono text-accent">{product.isg_sku}</span>
              {product.title ? (
                <>
                  {' '}
                  {' · '}
                  {product.title}
                </>
              ) : null}
            </p>
          )}
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto pr-2.5">
          {error && (
            <div className="m-4 p-3 rounded bg-danger/10 border border-danger/30 text-xs text-danger flex items-start gap-2">
              <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {!selectedProductId && !error && (
            <div className="m-4 p-4 rounded border border-border-subtle text-xs text-ink-muted text-center">
              Enter an ISG SKU above to inspect every intermediate calc value.
            </div>
          )}

          {loading && (
            <div className="m-4 space-y-2">
              <div className="h-5 bg-surface-3 rounded animate-pulse" />
              <div className="h-24 bg-surface-3 rounded animate-pulse" />
              <div className="h-32 bg-surface-3 rounded animate-pulse" />
              <div className="h-24 bg-surface-3 rounded animate-pulse" />
            </div>
          )}

          {!loading && extras && sampleRow && (
            <>
              {/* Section 1 — Cost Inputs */}
              <DebugPanelSection
                id="cost-inputs"
                title="Cost Inputs (per unit)"
                subtitle={`Safety buffer ${fmtPct(extras.safety_buffer_pct)} (applies per-path — see buffered_cost) · est. tax ${fmtPct(extras.estimated_tax_pct)}${extras.enforces_map ? ` · MAP enforced @ ${fmt$(extras.map_price)}` : ''}`}
              >
                <table className="w-full text-xs">
                  <tbody>
                    {[
                      ['cost_usd (raw distributor bill)', sampleRow.cost_usd, false],
                      ['supply_cost (junction, recomputed)', extras.supply_cost, false],
                      ['cost_product (persisted)', sampleRow.cost_product, false],
                      ['supplier_shipping', sampleRow.cost_supplier_shipping, false],
                      ['inbound_shipping (FBA / WFS only)', sampleRow.cost_inbound_shipping, false],
                      ['fba_prep_fee (FBA only)', sampleRow.cost_prep_fee, false],
                      ['misc_fee', sampleRow.cost_misc, false],
                      ['total_cost_per_unit', sampleRow.total_cost_per_unit, true],
                    ].map(([label, val, bold]) => (
                      <tr
                        key={String(label)}
                        className="border-b border-border-subtle/50 last:border-0"
                      >
                        <td
                          className={clsx(
                            'py-1 text-ink-secondary',
                            bold && 'font-semibold text-ink-bright'
                          )}
                        >
                          {String(label)}
                        </td>
                        <td
                          className={clsx(
                            'py-1 font-mono text-right',
                            bold ? 'text-ink-bright font-semibold' : 'text-ink-primary'
                          )}
                        >
                          {fmt$(val)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <p className="text-2xs text-ink-muted mt-2 italic">
                  Cost stack varies across paths only via inbound_shipping (FBA/WFS) and prep_fee
                  (FBA).
                </p>
              </DebugPanelSection>

              {/* Section 2 — Fee Breakdowns by Path */}
              <DebugPanelSection
                id="fee-breakdowns"
                title="Fee Breakdowns by Path"
                subtitle="referral_fee + path_fee should sum to persisted marketplace_fees within 1¢."
              >
                <PerPathTable
                  paths={visiblePaths}
                  ppByPath={ppByPath}
                  extras={extras}
                  rows={[
                    { label: 'referral_fee', kind: 'currency', getter: (pb) => pb.referral_fee },
                    {
                      label: 'path_fee',
                      kind: 'currency',
                      getter: (pb) => pb.path_fee,
                      sublabel: (pb) => pb.path_fee_label || '—',
                    },
                    {
                      label: 'total_fees (sum)',
                      kind: 'currency',
                      getter: (pb) => pb.total_fees,
                      bold: true,
                    },
                    {
                      label: 'persisted (drift check)',
                      kind: 'currency',
                      getter: (pb) => pb.persisted_marketplace_fees,
                    },
                  ]}
                />
              </DebugPanelSection>

              {/* Section 3 — Per-Path Intermediates */}
              <DebugPanelSection id="per-path-intermediates" title="Per-Path Intermediates">
                <PerPathTable
                  paths={visiblePaths}
                  ppByPath={ppByPath}
                  extras={extras}
                  rows={[
                    {
                      label: 'gross_revenue (sale_price)',
                      kind: 'currency',
                      getter: (pb) => pb.gross_revenue,
                    },
                    {
                      label: 'adjusted_revenue (× (1 − returns_rate))',
                      kind: 'currency',
                      getter: (pb) => pb.adjusted_revenue,
                    },
                    {
                      label: 'returns_cost_recomputed',
                      kind: 'currency',
                      getter: (pb) => pb.returns_cost_recomputed,
                    },
                    {
                      label: 'net_after_fees (= profit_per_unit)',
                      kind: 'currency',
                      getter: (pb) => pb.net_after_fees,
                      bold: true,
                    },
                  ]}
                />
              </DebugPanelSection>

              {/* Section 4 — Per-Path Final Values */}
              <DebugPanelSection
                id="per-path-final"
                title="Per-Path Final Values (persisted)"
                subtitle="total_cost recomputed by extras vs persisted in product_profitability — should match within 1¢ (Day 70 drift substrate)."
              >
                <PerPathTable
                  paths={visiblePaths}
                  ppByPath={ppByPath}
                  extras={extras}
                  rows={[
                    {
                      label: 'profit_per_unit',
                      kind: 'profitColor',
                      getter: (_pb, pp) => pp?.profit_per_unit ?? null,
                    },
                    {
                      label: 'ROI%',
                      kind: 'profitColor',
                      getter: (_pb, pp) => pp?.roi_pct ?? null,
                      formatter: 'pct',
                    },
                    {
                      label: 'profit_per_day_velocity',
                      kind: 'currency',
                      getter: (_pb, pp) => pp?.profit_per_day_velocity ?? null,
                    },
                    {
                      label: 'profit_per_day_safety',
                      kind: 'currency',
                      getter: (_pb, pp) => pp?.profit_per_day_safety ?? null,
                    },
                    {
                      label: 'days_locked_velocity',
                      kind: 'days',
                      getter: (_pb, pp) => pp?.days_credit_locked_velocity ?? null,
                    },
                    {
                      label: 'days_locked_safety',
                      kind: 'days',
                      getter: (_pb, pp) => pp?.days_credit_locked_safety ?? null,
                    },
                    {
                      label: 'effective_floor (break-even)',
                      kind: 'currency',
                      getter: (_pb, pp) => pp?.effective_floor ?? null,
                    },
                    {
                      label: 'total_cost (recomputed)',
                      kind: 'currency',
                      getter: (pb) => pb.total_cost_recomputed,
                    },
                    {
                      label: 'total_cost (persisted, drift check)',
                      kind: 'currency',
                      getter: (pb) => pb.persisted_total_cost,
                    },
                    {
                      label: 'below_map',
                      kind: 'currency',
                      getter: (pb) =>
                        pb.below_map === true ? 1 : pb.below_map === false ? 0 : null,
                      sublabel: () => 'TRUE=1 / FALSE=0 / NULL=—',
                    },
                  ]}
                />
              </DebugPanelSection>

              {/* Section 5 — Buy Score Components */}
              <DebugPanelSection
                id="buy-score"
                title="Buy Score Components"
                subtitle={`Default debug weights — not necessarily production weights. ${
                  extras.active_weights
                    ? Object.entries(extras.active_weights)
                        .map(([k, w]) => `${k}=${Number(w).toFixed(2)}`)
                        .join(' · ')
                    : 'No weights active.'
                }`}
              >
                {Object.keys(extras.buy_score_components).length === 0 ? (
                  <p className="text-xs text-ink-muted">
                    Buy Score components require active weights. Default debug weights are used.
                  </p>
                ) : (
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-2xs uppercase text-ink-muted">
                        <th className="text-left pb-1">Component</th>
                        <th className="text-right pb-1 font-mono">Raw</th>
                        <th className="text-right pb-1 font-mono">Normalized</th>
                        <th className="text-right pb-1 font-mono">Weight</th>
                        <th className="text-right pb-1 font-mono">Weighted</th>
                      </tr>
                    </thead>
                    <tbody>
                      {Object.entries(extras.buy_score_components).map(([k, c]) => (
                        <tr key={k} className="border-b border-border-subtle/50">
                          <td className="py-1 text-ink-secondary">
                            {k}
                            {c.penalty && (
                              <span className="ml-1 text-2xs text-danger font-mono">(−)</span>
                            )}
                            {!c.is_available && (
                              <span className="ml-1 text-2xs text-ink-muted font-mono">
                                (unwired)
                              </span>
                            )}
                          </td>
                          <td className="py-1 font-mono text-right text-ink-primary">
                            {fmtNum(c.raw, 2)}
                          </td>
                          <td className="py-1 font-mono text-right text-ink-secondary">
                            {fmtNum(c.normalized, 3)}
                          </td>
                          <td className="py-1 font-mono text-right text-ink-muted">
                            {fmtNum(c.weight, 2)}
                          </td>
                          <td className="py-1 font-mono text-right text-ink-bright">
                            {fmtNum(c.weighted, 3)}
                          </td>
                        </tr>
                      ))}
                      <tr className="border-t border-border">
                        <td className="py-1.5 font-semibold text-ink-bright">
                          Final Buy Score (extras parallel)
                        </td>
                        <td colSpan={3}></td>
                        <td className="py-1.5 font-mono text-right text-ink-bright font-semibold">
                          {fmtNum(extras.buy_score_final, 2)}
                        </td>
                      </tr>
                      <tr>
                        <td className="py-1 text-ink-secondary text-2xs">
                          Persisted (product_profitability.buy_score, most-profitable-path row)
                        </td>
                        <td colSpan={3}></td>
                        <td className="py-1 font-mono text-right text-ink-secondary">
                          {fmtNum(extras.persisted_buy_score, 2)}
                        </td>
                      </tr>
                    </tbody>
                  </table>
                )}
                <p className="text-2xs text-ink-muted mt-2 italic">
                  safety_cap_mode={String(extras.safety_cap_mode)} · drift between extras parallel
                  and persisted should be ≤0.01 absolute (Day 70 P3.3 normalized parity).
                </p>
              </DebugPanelSection>
            </>
          )}
        </div>
      </aside>
    </>
  );
}

// ── Per-path table primitive ──────────────────────────────────────────────

interface PerPathRowDef {
  label: string;
  kind: 'currency' | 'pct' | 'days' | 'profitColor';
  formatter?: 'pct';
  bold?: boolean;
  sublabel?: (pb: PerPathBreakdown) => string;
  getter: (pb: PerPathBreakdown, pp: PpRow | undefined) => number | null;
}

interface PerPathTableProps {
  paths: ReadonlyArray<{ key: string; label: string }>;
  ppByPath: Record<string, PpRow>;
  extras: ExtrasResponse;
  rows: PerPathRowDef[];
}

function PerPathTable({ paths, ppByPath, extras, rows }: PerPathTableProps): JSX.Element {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-xs">
        <thead>
          <tr className="text-2xs uppercase text-ink-muted">
            <th className="text-left pb-1 sticky left-0 bg-surface-1 pr-2">Metric</th>
            {paths.map((p) => (
              <th key={p.key} className="text-right pb-1 px-2">
                {p.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr key={row.label} className="border-b border-border-subtle/50 last:border-0">
              <td
                className={clsx(
                  'py-1 sticky left-0 bg-surface-1 pr-2',
                  row.bold ? 'font-semibold text-ink-bright' : 'text-ink-secondary'
                )}
              >
                {row.label}
                {row.sublabel && (
                  <span className="ml-1 text-2xs text-ink-muted font-mono">
                    ({/* per-path */ ''}
                    {row.sublabel(extras.per_path[paths[0]?.key] || ({} as PerPathBreakdown))})
                  </span>
                )}
              </td>
              {paths.map((p) => {
                const pb = extras.per_path[p.key] || ({} as PerPathBreakdown);
                const pp = ppByPath[p.key];
                const v = row.getter(pb, pp);
                let cls = 'font-mono text-right py-1 px-2 text-ink-primary';
                if (row.bold) cls += ' font-semibold text-ink-bright';
                if (row.kind === 'profitColor' && typeof v === 'number') {
                  cls += v > 0 ? ' text-success' : v < 0 ? ' text-danger' : '';
                }
                let txt: string;
                if (row.formatter === 'pct') txt = fmtPct(v);
                else if (row.kind === 'pct') txt = fmtPct(v);
                else if (row.kind === 'days') txt = fmtDays(v);
                else txt = fmt$(v);
                return (
                  <td key={p.key} className={cls}>
                    {txt}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
